import React, { useCallback, useMemo } from 'react';
import { type FieldValues, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Stack, Typography } from '@mui/material';
import { type TFunction } from 'i18next';
import * as yup from 'yup';

import { FolderType } from '@/api/generated';
import CreateProcessModalIcon from '@/assets/icons/square-briefcase-color.svg?react';
import Field from '@/components/fields/Field';
import LocationTreeSelect from '@/components/fields/Location/LocationTreeSelect';
import TextInput from '@/components/fields/TextInput';
import LoadingButton from '@/components/LoadingButton';
import { OrganizationStructureProvider } from '@/hooks/useOrganizationStructure';
import { ROUTER_PATHS } from '@/root/linker';
import { borders } from '@/theme/constants';
import OrganizationDropdownSelect from '@/views/fields/OrganizationDropdownSelect/OrganizationDropdownSelect';
import SettingsModalForm from '@/views/SettingsModalForm/SettingsModalForm';

const allowedLocationTypes = [FolderType.FOLDER, FolderType.SUB_FOLDER];

const buildSchema = (t: TFunction) =>
  yup.object().shape({
    name: yup.string().required(
      t('validation:mandatory', {
        field: t('processes:layouts.processCreate.processName')
      })
    ),
    organization: yup.string().required(
      t('validation:mandatory', {
        field: t('processes:layouts.processCreate.organization')
      })
    ),
    location: yup.string().required(
      t('validation:mandatory', {
        field: t('processes:layouts.processCreate.location')
      })
    )
  });

interface CreateProcessModalData {
  name?: string;
  organization?: string;
}

const DEFAULT_VALUES: CreateProcessModalData = {
  name: ''
};

interface CreateProcessModalProps {
  onClose: () => void;
  defaultOrganizationSlug?: string;
  defaultLocationSlug?: string;
}

export const CreateProcessModal: React.FC<CreateProcessModalProps> = ({
  onClose,
  defaultOrganizationSlug,
  defaultLocationSlug
}) => {
  const { t } = useTranslation(['processes', 'common', 'validation']);
  const navigate = useNavigate();

  const onSubmit = useCallback(
    async (data: FieldValues) => navigate(ROUTER_PATHS.PROCESS_NEW, { state: data }),
    [navigate]
  );

  const schema = useMemo(() => buildSchema(t), [t]);

  const defaultValues = useMemo(
    () => ({
      ...DEFAULT_VALUES,
      organization: defaultOrganizationSlug,
      location: defaultLocationSlug
    }),
    [defaultOrganizationSlug, defaultLocationSlug]
  );

  return (
    <SettingsModalForm
      defaultValues={defaultValues}
      head={
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          sx={{
            py: 2.25
          }}
        >
          <CreateProcessModalIcon />
          <Typography variant="h3" sx={{ fontWeight: 600 }}>
            {t('layouts.processCreate.title')}
          </Typography>
        </Stack>
      }
      onClose={onClose}
      onSave={onSubmit}
      validation={schema}
      locationsMatch={() => true}
      sx={{
        '&.CreateProcessModal-root .SettingsModalForm-root': {
          maxWidth: 600,
          minHeight: 'auto',
          height: 'auto'
        },
        '&.CreateProcessModal-root .MuiDialogTitle-root': {
          py: 0,
          borderBottom: borders.default,
          borderColor: 'grey.300'
        }
      }}
      className="CreateProcessModal-root"
    >
      <CreateProcessModalContent defaultOrganizationSlug={defaultOrganizationSlug} />
    </SettingsModalForm>
  );
};

const CreateProcessModalContent = ({ defaultOrganizationSlug }: { defaultOrganizationSlug?: string }) => {
  const { t } = useTranslation(['processes', 'common']);
  const { watch } = useFormContext();
  const organization = watch('organization');
  const { isValid } = useFormState();

  return (
    <OrganizationStructureProvider organizationSlug={organization}>
      <Stack
        sx={{
          p: 3,
          '& .MuiFormControl-root': {
            p: 0
          }
        }}
        gap={3}
      >
        <Field
          name="name"
          label={t('layouts.processCreate.processName')}
          component={TextInput}
          withFormControl
          innerProps={{ markAsRequired: true }}
        />

        <Stack>
          <Field
            name="organization"
            label={t('layouts.processCreate.availableIn')}
            component={OrganizationDropdownSelect}
            innerProps={{
              isMulti: false,
              placeholder: 'Search organizations...',
              markAsRequired: true,
              usePortal: true,
              isDisabled: !!defaultOrganizationSlug
            }}
            withFormControl
          />

          <Field
            name="location"
            label=""
            component={LocationTreeSelect}
            withFormControl
            innerProps={{
              organizationSlug: organization,
              allowedTypes: allowedLocationTypes
            }}
          />
        </Stack>
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        py={2}
        px={3}
        sx={{
          borderTop: borders.default,
          borderColor: 'grey.300'
        }}
      >
        <LoadingButton disabled={!isValid} type="submit" sx={{ minWidth: 100 }} variant="contained">
          {t('common:actions.next')}
        </LoadingButton>
      </Stack>
    </OrganizationStructureProvider>
  );
};
